<template>
  <div
    :data-cy="generateAutomationTag(sectionTag, componentTag)"
    :class="{ 'bar-border': useBorder }"
  >
    <span v-for="n in 5" :key="n">
      <v-icon
        v-if="useFaces && useColors"
        :data-cy="generateAutomationTag(sectionTag, componentTag, `face-color-${n}`)"
        :icon="faces[n]"
        :size="size"
        variant="text"
        :color="getColor(n)"
        @mouseenter="hoveredRating = n"
        @mouseleave="hoveredRating = 0"
        @click="setRating(n)"
      ></v-icon>
      <v-icon
        v-else-if="useFaces"
        :data-cy="generateAutomationTag(sectionTag, componentTag, `face-${n}`)"
        :icon="faces[n]"
        :size="size"
        variant="text"
        :color="getColor(n)"
        @mouseenter="hoveredRating = n"
        @mouseleave="hoveredRating = 0"
        @click="setRating(n)"
      ></v-icon>
      <v-icon
        v-else-if="useColors"
        :data-cy="generateAutomationTag(sectionTag, componentTag, `star-color-${n}`)"
        :icon="icons.mdiStar"
        :size="size"
        variant="text"
        :color="getColor(n)"
        @mouseenter="hoveredRating = n"
        @mouseleave="hoveredRating = 0"
        @click="setRating(n)"
      ></v-icon>
      <v-icon
        v-else
        :data-cy="generateAutomationTag(sectionTag, componentTag, `star-${n}`)"
        :icon="icons.mdiStar"
        :size="size"
        variant="text"
        :color="getColor(n)"
        @mouseenter="hoveredRating = n"
        @mouseleave="hoveredRating = 0"
        @click="setRating(n)"
      ></v-icon>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  mdiStar,
  mdiEmoticonCryOutline,
  mdiEmoticonSadOutline,
  mdiEmoticonNeutralOutline,
  mdiEmoticonHappyOutline,
  mdiEmoticonTongueOutline
} from '@mdi/js'
import { useRatingColors } from '@/composables/RatingColors.ts'
import { colors } from '@/plugins/vuetify'
import { useCypressAutomation } from '@/composables/Cypress'

const props = defineProps({
  sectionTag: {
    type: String,
    required: false,
    default: 'unspecified'
  },
  modelValue: Number,
  size: {
    type: String,
    default: '20px'
  },
  useFaces: {
    type: Boolean,
    default: false
  },
  useColors: {
    type: Boolean,
    default: false
  },
  useBorder: {
    type: Boolean,
    default: true
  }
})

const componentTag: string = 'ratings-bar'
const { generateAutomationTag } = useCypressAutomation()

const hoveredRating = ref(0)

const emit = defineEmits(['update:modelValue'])

const rating = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const setRating = (newRating: number) => {
  rating.value = newRating
}

const getColor = (n: number) => {
  if (hoveredRating.value >= n || rating.value >= n) {
    return props.useColors ? iconColors[n] : colors.yellow.yellow600
  } else {
    return colors.neutral.neutral200
  }
}

const faces = {
  5: mdiEmoticonTongueOutline,
  4: mdiEmoticonHappyOutline,
  3: mdiEmoticonNeutralOutline,
  2: mdiEmoticonSadOutline,
  1: mdiEmoticonCryOutline
}

const iconColors = {
  5: useRatingColors().getColor(5),
  4: useRatingColors().getColor(4),
  3: useRatingColors().getColor(3),
  2: useRatingColors().getColor(2),
  1: useRatingColors().getColor(1)
}

const icons = {
  mdiStar
}
</script>

<style lang="scss">
.bar-border {
  border: 1px solid rgb(200, 200, 200);
}
</style>
